import { useMemo } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import type { RecipeApiItem } from "@recipevault/shared";
import { db } from "../../sync/db.js";
import { makeRecipeFuse } from "../../shared/fuse.js";

export type LibrarySort = "recent" | "alpha" | "rating" | "quickest" | "easiest" | "most-cooked" | "top-rated";

const DIFFICULTY_ORDER: Record<string, number> = { easy: 1, medium: 2, hard: 3 };

/**
 * Sort comparator factory. Each returns a (a, b) => number suitable for Array.sort.
 * For nullable fields ("nulls last" semantics): null/undefined values sink to the
 * bottom regardless of sort direction.
 */
export const COMPARATORS: Record<LibrarySort, (a: RecipeApiItem, b: RecipeApiItem) => number> = {
  recent: (a, b) => b.updatedAt.localeCompare(a.updatedAt),
  alpha: (a, b) => a.title.localeCompare(b.title),
  rating: (a, b) => nullsLast(a.rating, b.rating, (x, y) => y - x), // higher first
  quickest: (a, b) => nullsLast(a.totalTimeMin, b.totalTimeMin, (x, y) => x - y), // smaller first
  easiest: (a, b) => nullsLast(
    a.difficulty == null ? null : DIFFICULTY_ORDER[a.difficulty] ?? null,
    b.difficulty == null ? null : DIFFICULTY_ORDER[b.difficulty] ?? null,
    (x, y) => x - y,
  ),
  "most-cooked": (a, b) => nullsLast(
    a.stats && a.stats.cookCount > 0 ? a.stats.cookCount : null,
    b.stats && b.stats.cookCount > 0 ? b.stats.cookCount : null,
    (x, y) => y - x, // higher first
  ),
  "top-rated": (a, b) => nullsLast(
    a.stats?.dishRating.avg ?? null,
    b.stats?.dishRating.avg ?? null,
    (x, y) => y - x, // higher first
  ),
};

function nullsLast<T>(
  a: T | null,
  b: T | null,
  cmp: (x: T, y: T) => number,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1; // a sinks
  if (b == null) return -1; // b sinks
  return cmp(a, b);
}

export interface LibraryFilters {
  tagIds: string[];
  categoryIds: string[];
  cuisineIds: string[]; // multi-select even though each recipe has a single cuisineId
  dietFlags: string[];
  // Total-time quick-filter chip (Plan 13, Task 10): ≤30/≤45/≤60. Omitted (not
  // 0/null) when no cap is active — exactOptionalPropertyTypes means "unset"
  // is expressed by the key's absence, never an explicit `undefined`.
  maxTotalTimeMin?: number;
}

interface UseRecipesOpts {
  filters?: LibraryFilters;
  search?: string;
  sort?: LibrarySort;
}

interface UseRecipesResult {
  recipes: RecipeApiItem[];
  status: "loading" | "ready";
}

export function matchesFilters(r: RecipeApiItem, f: LibraryFilters): boolean {
  // AND across kinds, OR within a kind.
  if (f.tagIds.length > 0 && !r.taxonomy.tagIds.some((id) => f.tagIds.includes(id))) return false;
  if (f.categoryIds.length > 0 && !r.taxonomy.categoryIds.some((id) => f.categoryIds.includes(id))) return false;
  if (f.cuisineIds.length > 0 && !(r.taxonomy.cuisineId && f.cuisineIds.includes(r.taxonomy.cuisineId))) return false;
  if (f.dietFlags.length > 0 && !r.taxonomy.dietFlags.some((id) => f.dietFlags.includes(id))) return false;
  // A recipe with no totalTimeMin is excluded once a time cap is set — "unknown"
  // never counts as "fits under the cap".
  if (f.maxTotalTimeMin != null && !(r.totalTimeMin != null && r.totalTimeMin <= f.maxTotalTimeMin)) return false;
  return true;
}

const EMPTY_FILTERS: LibraryFilters = { tagIds: [], categoryIds: [], cuisineIds: [], dietFlags: [] };

export function useRecipes(opts: UseRecipesOpts): UseRecipesResult {
  const all = useLiveQuery(
    async () => db.recipes.toArray(),
    [],
    undefined as RecipeApiItem[] | undefined,
  );

  const filters = opts.filters ?? EMPTY_FILTERS;
  const sort = opts.sort ?? "top-rated";
  const search = opts.search?.trim() ?? "";

  const recipes = useMemo(() => {
    if (!all) return [];
    // `pending` = an unconfirmed photo-import draft — hidden from the library and
    // search until the user confirms it in review. Legacy rows (no status) show.
    let result = all.filter((r) => !r.deletedAt && r.status !== "pending" && matchesFilters(r, filters));

    if (search.length > 0) {
      const fuse = makeRecipeFuse(result);
      result = fuse.search(search).map((m) => m.item);
      // Fuse already ranks; ignore further sort when searching.
      return result;
    }

    const cmp = COMPARATORS[sort] ?? COMPARATORS["top-rated"];
    return [...result].sort(cmp);
  }, [all, filters, search, sort]);

  return {
    recipes,
    status: all === undefined ? "loading" : "ready",
  };
}
